/**
 * Instrument create bodies (web brief U6): the v2 (multi-protocol) body from
 * the Map step's rows, and the v1 (legacy Modbus) body a gateway without
 * config schema v2 still needs.
 *
 * v2 = `{name, poll_sec, link: {driver, params}, params: [{metric, address,
 * data_type, scale?, offset?, data_kind?, is_cycle_counter?, poll_sec?,
 * deadband?}]}`. Mixing in any v1 field (protocol, ip, slave_id, register, kind,
 * reg_count, word_order) is a 400 `mixed_body`, so the two builders never share
 * a param shape.
 *
 * Each point carries its row's own poll interval (whole seconds, 1 to 86400);
 * the body's `poll_sec` is the default for a point without one. Both bodies
 * take a per-point `poll_sec`; only v2 takes a per-point deadband.
 *
 * A Modbus row keeps its register fields (address text, register space,
 * reg_count, p_type, word_order) and becomes a v2 address {space, register,
 * word_order}; the register text is read the way the platform reads it
 * (instruments_v2.go parseRegisterRef: 40001+ and 4001-4999 are holding
 * references). A non-Modbus row carries its driver address and type as picked.
 */

import type {
  CreateInstrumentBody,
  CreateInstrumentV2Body,
  InstrumentParamInput,
  InstrumentPointV2,
  PointDataType,
} from "@/lib/api/things";
import type { TagRow } from "@/components/iot/ThingTagRows";
import { DRIVER, isModbusDriver } from "./drivers";
import { isPollSec } from "./instrument-points";

/**
 * A legacy register text as a 0-based offset, the platform's rule: a "+node"
 * suffix is dropped, 40001+ and 4001-4999 are holding references, anything
 * else is the offset. Null when it is not a register.
 */
export function parseRegisterRef(text: string): number | null {
  let s = text.trim();
  const plus = s.indexOf("+");
  if (plus >= 0) s = s.slice(0, plus).trim();
  if (!/^\d+$/.test(s)) return null;
  let n = Number(s);
  if (n >= 40001) n -= 40001;
  else if (n >= 4001 && n <= 4999) n -= 4001;
  return n <= 65535 ? n : null;
}

/**
 * A 0-based register as legacy register text that {@link parseRegisterRef}
 * reads back as the same register (the platform's legacyRegisterText):
 * offsets in the ranges read as references are written 40001-based.
 */
export function legacyRegisterText(register: number): string {
  if ((register >= 4001 && register <= 4999) || register >= 40001) return String(40001 + register);
  return String(register);
}

/** The v1 Modbus wire type of a row (int16 / int32 / float32 / double), as go-live always sent it. */
export function modbusDataType(row: TagRow): "int16" | "int32" | "float32" | "double" {
  if (row.p_type === 5) return "double";
  if (row.p_type === 4) return "float32";
  if (row.p_type === 3) return "int32";
  if (row.p_type === 1 || row.p_type === 2) return "int16";
  if (row.data_type === "number" || row.data_type === "percentage") return "float32";
  return (row.reg_count ?? 1) >= 2 ? "int32" : "int16";
}

/** A Modbus row's v2 data type: coils / discrete inputs are bool, double is float64, a uint16 / uint32 hint is kept. */
export function modbusPointType(row: TagRow): PointDataType {
  if (row.register_kind === "coil" || row.register_kind === "discrete") return "bool";
  const t = modbusDataType(row);
  if (t === "double") return "float64";
  if (t === "int16" && row.point_type === "uint16") return "uint16";
  if (t === "int32" && row.point_type === "uint32") return "uint32";
  return t;
}

const WIDE: ReadonlySet<PointDataType> = new Set(["int32", "uint32", "int64", "uint64", "float32", "float64"]);

/** Named rows only: an unnamed row is a blank the operator did not fill. */
function named(rows: TagRow[]): TagRow[] {
  return rows.filter((r) => r.name.trim().length > 0);
}

/**
 * A row's "poll" as whole seconds (a fraction rounded down), or undefined when
 * it is blank, not a number, or outside 1 to 86400: the point then takes the
 * instrument's.
 */
export function rowPollSec(row: Pick<TagRow, "poll_sec">): number | undefined {
  const s = Math.floor(Number(row.poll_sec));
  return isPollSec(s) ? s : undefined;
}

/** The point-level fields every driver shares (transform, kind, cycle counter, poll, deadband, heartbeat). */
function common(row: TagRow, type: PointDataType): Partial<InstrumentPointV2> {
  const out: Partial<InstrumentPointV2> = {};
  if (row.scale !== undefined && row.scale !== 1) out.scale = row.scale;
  if (row.offset !== undefined && row.offset !== 0) out.offset = row.offset;
  const kind = type === "string" ? "string_label" : row.data_kind;
  if (kind) out.data_kind = kind;
  if (row.is_cycle_counter) out.is_cycle_counter = true;
  const poll = rowPollSec(row);
  if (poll !== undefined) out.poll_sec = poll;
  if (row.deadband !== undefined && row.deadband > 0) out.deadband = row.deadband;
  if (row.max_silence_sec !== undefined) out.max_silence_sec = row.max_silence_sec;
  return out;
}

/** A Modbus row as a v2 point, or null when its register does not parse. */
export function modbusRowToPoint(row: TagRow): InstrumentPointV2 | null {
  const register = parseRegisterRef(row.address);
  if (register === null) return null;
  const type = modbusPointType(row);
  const address: Record<string, unknown> = { space: row.register_kind ?? "holding", register };
  if (WIDE.has(type) && row.word_order && row.word_order !== "big") address.word_order = row.word_order;
  return { metric: row.name.trim(), address, data_type: type, ...common(row, type) };
}

/** A non-Modbus row as a v2 point (its picked driver address and type). */
export function driverRowToPoint(row: TagRow): InstrumentPointV2 {
  const type = row.point_type ?? "int16";
  return { metric: row.name.trim(), address: row.point_address ?? {}, data_type: type, ...common(row, type) };
}

/**
 * The Map step's rows as v2 points for a driver. Unnamed rows are dropped; a
 * Modbus row whose register does not parse is dropped too (the create would
 * 422 on it) and reported in `skipped`.
 */
export function rowsToPoints(driver: string, rows: TagRow[]): { points: InstrumentPointV2[]; skipped: string[] } {
  const points: InstrumentPointV2[] = [];
  const skipped: string[] = [];
  for (const r of named(rows)) {
    if (isModbusDriver(driver)) {
      const p = modbusRowToPoint(r);
      if (p) points.push(p);
      else skipped.push(r.name.trim());
    } else {
      points.push(driverRowToPoint(r));
    }
  }
  return { points, skipped };
}

export interface V2BodyInput {
  name: string;
  driver: string;
  link: Record<string, unknown>;
  rows: TagRow[];
  pollSec?: number;
  equipmentType?: string;
}

/** The v2 create body. */
export function buildV2Body(input: V2BodyInput): CreateInstrumentV2Body {
  const { points } = rowsToPoints(input.driver, input.rows);
  return {
    name: input.name.trim(),
    ...(input.equipmentType ? { equipment_type: input.equipmentType } : {}),
    ...(input.pollSec ? { poll_sec: input.pollSec } : {}),
    link: { driver: input.driver, params: input.link },
    params: points,
  };
}

/**
 * Named rows carrying an address → the v1 Modbus param map (as before v2, plus
 * each row's own poll interval; the v1 body has no per-point deadband).
 */
export function buildV1Params(rows: TagRow[]): InstrumentParamInput[] {
  return named(rows)
    .filter((r) => r.address.trim().length > 0)
    .map((r) => ({
      metric: r.name.trim(),
      register: r.address.trim(),
      kind: r.register_kind,
      reg_count: r.reg_count,
      data_type: modbusDataType(r),
      data_kind: r.data_kind,
      scale: r.scale,
      offset: r.offset,
      is_cycle_counter: r.is_cycle_counter || undefined,
      // Set on the mapping step for a 2+ register value; "big" (ABCD) is the
      // backend default, so it is omitted rather than sent.
      word_order: r.word_order && r.word_order !== "big" ? r.word_order : undefined,
      poll_sec: rowPollSec(r),
    }));
}

export interface V1BodyInput {
  name: string;
  driver: string;
  /** The link as the connection form (or the scan) has it: host / port / unit_id, or the serial settings. */
  link: Record<string, unknown>;
  rows: TagRow[];
  pollSec?: number;
}

const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
const text = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

/**
 * The v1 (legacy Modbus) body, for a gateway that does not accept config
 * schema v2 (a VSync, a Blackbox, GatewayPro <= 0.2) or a server without the
 * drivers registry: exactly what go-live sent before multi-protocol, with the
 * link read from the connection form.
 */
export function buildV1Body(input: V1BodyInput): CreateInstrumentBody {
  const rtu = input.driver === DRIVER.MODBUS_RTU;
  const l = input.link;
  const parity = text(l.parity);
  const dataBits = num(l.data_bits);
  const stopBits = num(l.stop_bits);
  return {
    name: input.name.trim(),
    protocol: rtu ? "rtu" : "tcp",
    slave_id: num(l.unit_id),
    ...(rtu
      ? {
          serial_port: text(l.serial_port),
          baud: num(l.baud),
          data_bits: dataBits === 7 || dataBits === 8 ? dataBits : undefined,
          parity: parity === "N" || parity === "E" || parity === "O" ? parity : undefined,
          stop_bits: stopBits === 1 || stopBits === 2 ? stopBits : undefined,
        }
      : { ip: text(l.host), port: num(l.port) }),
    poll_sec: input.pollSec,
    params: buildV1Params(input.rows),
  };
}

/**
 * Whether a device on this gateway is created with the v1 body: a Modbus
 * driver on a gateway that does not accept config schema v2, or any Modbus
 * device when the server lists no drivers (it predates the v2 create).
 * Everything else is v2.
 */
export function needsV1Body(driver: string, opts: { gatewaySpeaksV2: boolean; driversListed: boolean }): boolean {
  return isModbusDriver(driver) && (!opts.driversListed || !opts.gatewaySpeaksV2);
}

/** One point in the gateway panel's v2 instrument form. */
export interface FormPoint {
  key: string;
  metric: string;
  address: Record<string, unknown>;
  data_type: PointDataType;
  is_cycle_counter?: boolean;
  scale?: number;
  offset?: number;
  /** The point's own poll interval (whole seconds); unset takes the form's. */
  poll_sec?: number;
  deadband?: number;
}

/** The form's points as v2 points (unnamed ones dropped; a string point gets string_label). */
export function formPointsToV2(points: FormPoint[]): InstrumentPointV2[] {
  return points
    .filter((p) => p.metric.trim())
    .map((p) => ({
      metric: p.metric.trim(),
      address: p.address,
      data_type: p.data_type,
      ...(p.data_type === "string" ? { data_kind: "string_label" } : {}),
      ...(p.scale !== undefined && p.scale !== 1 ? { scale: p.scale } : {}),
      ...(p.offset !== undefined && p.offset !== 0 ? { offset: p.offset } : {}),
      ...(p.is_cycle_counter ? { is_cycle_counter: true } : {}),
      ...(p.poll_sec !== undefined && isPollSec(p.poll_sec) ? { poll_sec: p.poll_sec } : {}),
      ...(p.deadband !== undefined && p.deadband > 0 ? { deadband: p.deadband } : {}),
    }));
}

/**
 * A VSync equipment template (v1 Modbus params) as v2 Modbus points: the
 * register text read the platform's way, the space from `kind`, double as
 * float64, and a non-default word order kept on the address.
 */
export function templateToFormPoints(
  params: Array<{
    metric: string;
    register: string;
    kind?: string;
    data_type?: string;
    word_order?: string;
    is_cycle_counter?: boolean;
  }>,
): FormPoint[] {
  return params.flatMap((p, i) => {
    const register = parseRegisterRef(p.register);
    if (register === null) return [];
    const space = p.kind === "input" || p.kind === "coil" || p.kind === "discrete" ? p.kind : "holding";
    const bit = space === "coil" || space === "discrete";
    const type: PointDataType = bit
      ? "bool"
      : p.data_type === "double"
        ? "float64"
        : p.data_type === "int32" || p.data_type === "float32" || p.data_type === "int16"
          ? p.data_type
          : "float32";
    const address: Record<string, unknown> = { space, register };
    if (WIDE.has(type) && p.word_order && p.word_order !== "big") address.word_order = p.word_order;
    return [{ key: `tpl_${i}`, metric: p.metric, address, data_type: type, is_cycle_counter: p.is_cycle_counter || undefined }];
  });
}
